import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    // Let AuthCallback handle authentication first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      console.log('Not authenticated');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data.user);
    return response.data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, name, password, role = 'viewer') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      name,
      password,
      role,
    });
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const hasTabAccess = (tabName) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    return user.tab_permissions?.[tabName] === true;
  };

  // True when the logged-in user is a read-only "viewer". Super-admins are
  // never read-only. UI should hide write/edit/delete affordances and any
  // server-side mutation will be blocked by the read_only_guard middleware.
  const isReadOnly = Boolean(user && !user.is_super_admin && user.is_read_only);
  // Convenience helper for read-only checks anywhere in the UI. Call sites:
  //   {canWrite() && <Button …>Edit</Button>}
  const canWrite = () => !isReadOnly;

  // Hide every element marked [data-write-only] (used on Add/Edit/Delete
  // buttons across the app) via a body-level class. Gives us a one-line
  // global guard without rewriting each page; backend middleware still
  // blocks the API even if someone overrides CSS.
  useEffect(() => {
    document.body.classList.toggle('app-read-only', isReadOnly);
    return () => document.body.classList.remove('app-read-only');
  }, [isReadOnly]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleLogin,
        exchangeSession,
        register,
        logout,
        checkAuth,
        hasTabAccess,
        isReadOnly,
        canWrite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
